import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormReturn } from "react-hook-form";
import { boolean, enum as zEnum, object } from "zod";

import { ContentLinkVisibilityValues, Organization, TaggedContentSettings } from "@/types";

/**
 * The organization's tagged-content settings (Extras tab / step): one switch per
 * content kind deciding whether other entities may tag that kind onto this
 * organization, each with the default visibility a fresh tag of that kind gets.
 * See docs/content-tagging.md.
 */
export type OrganizationExtrasFormData = TaggedContentSettings;

export type OrganizationExtrasFormControl = {
  hasBeenSubmitted: boolean;
  isValid: boolean;
  isDirty: boolean;
  getSubmittedValues: () => OrganizationExtrasFormData;
  resetWithCurrentValues: () => void;
  resetForNewOrganization: (newValues: Organization) => void;
  _: {
    toDefault: () => void;
    submit: () => void;
    form: UseFormReturn<OrganizationExtrasFormData>;
  };
};

/**
 * A brand-new organization accepts tags of both kinds and shows them straight
 * away — the permissive behaviour that predates the per-kind split.
 */
const DEFAULT_VALUES: OrganizationExtrasFormData = {
  allowNewsTagging: true,
  defaultTaggedNewsVisibility: "public",
  allowGalleryTagging: true,
  defaultTaggedGalleryVisibility: "public",
};

const getDefaultValuesForOrganization = (
  organization: Organization
): OrganizationExtrasFormData => ({
  allowNewsTagging: organization.allowNewsTagging,
  defaultTaggedNewsVisibility: organization.defaultTaggedNewsVisibility,
  allowGalleryTagging: organization.allowGalleryTagging,
  defaultTaggedGalleryVisibility: organization.defaultTaggedGalleryVisibility,
});

/**
 * Every field is always present, so the schema has no conditional rules: the
 * visibility select of a switched-off kind keeps its value and is simply not
 * acted on by the backend until the switch goes back on.
 */
export const organizationExtrasSchema = object({
  allowNewsTagging: boolean(),
  defaultTaggedNewsVisibility: zEnum(ContentLinkVisibilityValues),
  allowGalleryTagging: boolean(),
  defaultTaggedGalleryVisibility: zEnum(ContentLinkVisibilityValues),
});

export const useOrganizationExtrasForm = ({
  onSubmit,
  organization,
  disabled,
}: {
  onSubmit: (data: OrganizationExtrasFormData) => void;
  organization?: Organization;
  /** Disables the whole form (read-only mode) — RHF keeps `isDirty` false and ignores submits. */
  disabled?: boolean;
}): OrganizationExtrasFormControl => {
  const resolvedDefaultValues = organization
    ? getDefaultValuesForOrganization(organization)
    : DEFAULT_VALUES;

  const form = useForm<OrganizationExtrasFormData>({
    resolver: zodResolver(organizationExtrasSchema),
    defaultValues: resolvedDefaultValues,
    disabled,
  });

  const submit = form.handleSubmit((data) => onSubmit(data));

  return {
    hasBeenSubmitted: form.formState.submitCount > 0,
    isDirty: form.formState.isDirty,
    isValid: form.formState.isValid,
    getSubmittedValues: () => form.getValues(),
    resetWithCurrentValues: () => form.reset(form.getValues()),
    resetForNewOrganization: (newValues: Organization) => {
      form.reset(getDefaultValuesForOrganization(newValues));
    },
    _: { toDefault: () => form.reset(resolvedDefaultValues), form, submit },
  };
};
